import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Trash2,
  RefreshCw,
  AlertTriangle,
  CheckSquare,
  Square,
  ArrowUpDown,
  Shield,
  Archive
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppStore } from '../stores/appStore';
import { api } from '../lib/api';
import { Case } from '../types';
import Button from '../components/ui/Button';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';

type SortOption = 'newest' | 'oldest' | 'name-asc' | 'name-desc';

export default function AdminCases() {
  const navigate = useNavigate();
  const currentUser = useAppStore((state) => state.currentUser);
  const queryClient = useQueryClient();

  // State
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState<{
    type: 'restore' | 'hard-delete' | 'bulk-hard-delete' | null;
    caseId?: string;
    projectName?: string;
  }>({ type: null });

  // Check if user is admin
  const isAdmin = useMemo(() => {
    if (!currentUser?.email) return false;
    const email = currentUser.email.toLowerCase();
    return (
      email.includes('admin') ||
      email.endsWith('@graphsolution.ai') ||
      email.endsWith('@amplinth.com')
    );
  }, [currentUser]);

  // Fetch inactive (soft-deleted) projects
  const { data: projectsResponse, isLoading } = useQuery<{ cases: Case[]; total: number }>({
    queryKey: ['admin-inactive-projects'],
    queryFn: async () => {
      const response = await api.get<{ cases: Case[]; total: number }>('/cases/inactive');
      return response;
    },
    enabled: isAdmin,
  });

  // Apply sorting
  const sortedProjects = useMemo(() => {
    let result = projectsResponse?.cases || [];

    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });

    return result;
  }, [projectsResponse?.cases, sortBy]);

  // Restore mutation
  const restoreMutation = useMutation({
    mutationFn: (caseId: string) =>
      api.post(`/cases/${caseId}/restore`),
    onSuccess: (_, caseId) => {
      queryClient.invalidateQueries({ queryKey: ['admin-inactive-projects'] });
      queryClient.invalidateQueries({ queryKey: ['user-projects'] });
      toast.success('Case restored successfully');
      setShowConfirmDialog({ type: null });
      setSelectedProjects(prev => {
        const next = new Set(prev);
        next.delete(caseId);
        return next;
      });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || error?.message || 'Failed to restore project');
    },
  });

  // Hard delete mutation
  const hardDeleteMutation = useMutation({
    mutationFn: (caseId: string) =>
      api.delete(`/cases/${caseId}/hard`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-inactive-projects'] });
      toast.success('Case permanently deleted');
      setShowConfirmDialog({ type: null });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || error?.message || 'Failed to permanently delete project');
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: ({ caseIds, hardDelete }: { caseIds: string[]; hardDelete: boolean }) =>
      api.post('/cases/bulk-delete', { case_ids: caseIds, hard_delete: hardDelete }),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['admin-inactive-projects'] });
      const { success_count, failed_count } = response;
      if (failed_count > 0) {
        toast.warning(`Deleted ${success_count} projects, ${failed_count} failed`);
      } else {
        toast.success(`Successfully deleted ${success_count} projects`);
      }
      setSelectedProjects(new Set());
      setShowConfirmDialog({ type: null });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || error?.message || 'Bulk delete failed');
    },
  });

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedProjects.size === sortedProjects.length) {
      setSelectedProjects(new Set());
    } else {
      setSelectedProjects(new Set(sortedProjects.map(p => p.caseId || p.id)));
    }
  };

  const toggleSelectProject = (caseId: string) => {
    setSelectedProjects(prev => {
      const next = new Set(prev);
      if (next.has(caseId)) {
        next.delete(caseId);
      } else {
        next.add(caseId);
      }
      return next;
    });
  };

  // Action handlers
  const handleRestore = (caseId: string, projectName: string) => {
    setShowConfirmDialog({ type: 'restore', caseId, projectName });
  };

  const handleHardDelete = (caseId: string, projectName: string) => {
    setShowConfirmDialog({ type: 'hard-delete', caseId, projectName });
  };

  const handleBulkHardDelete = () => {
    if (selectedProjects.size === 0) {
      toast.error('No projects selected');
      return;
    }
    setShowConfirmDialog({ type: 'bulk-hard-delete' });
  };

  const confirmAction = () => {
    if (!showConfirmDialog.type) return;

    switch (showConfirmDialog.type) {
      case 'restore':
        if (showConfirmDialog.caseId) {
          restoreMutation.mutate(showConfirmDialog.caseId);
        }
        break;
      case 'hard-delete':
        if (showConfirmDialog.caseId) {
          hardDeleteMutation.mutate(showConfirmDialog.caseId);
        }
        break;
      case 'bulk-hard-delete':
        bulkDeleteMutation.mutate({
          caseIds: Array.from(selectedProjects),
          hardDelete: true,
        });
        break;
    }
  };

  // Non-admin view
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardBody className="text-center p-8">
            <Shield className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">
              Admin Access Required
            </h2>
            <p className="text-neutral-600 mb-6">
              You need administrator privileges to access this page.
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Shield className="w-10 h-10 text-secondary-600" />
          <div>
            <h1 className="text-[40px] font-bold text-neutral-900 mb-2">Admin: Deleted Cases</h1>
            <p className="text-neutral-600">
              Manage soft-deleted projects - restore or permanently delete
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 bg-neutral-100 rounded-lg flex items-center justify-center">
              <Archive className="w-6 h-6 text-neutral-600" />
            </div>
            <div>
              <p className="text-sm text-neutral-600">Deleted Cases</p>
              <p className="text-2xl font-bold text-neutral-900">
                {projectsResponse?.total || 0}
              </p>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
              <CheckSquare className="w-6 h-6 text-secondary-600" />
            </div>
            <div>
              <p className="text-sm text-neutral-600">Selected</p>
              <p className="text-2xl font-bold text-neutral-900">
                {selectedProjects.size}
              </p>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-neutral-600">Status</p>
              <p className="text-sm font-semibold text-neutral-900">
                Admin Mode
              </p>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Projects List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-neutral-900">Deleted Cases</h2>
            <div className="flex items-center gap-4">
              {/* Bulk Actions */}
              {selectedProjects.size > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkHardDelete}
                  className="text-error hover:bg-error-50 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Permanently Delete ({selectedProjects.size})
                </Button>
              )}

              {/* Sort Dropdown */}
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-neutral-600" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="px-3 py-2 bg-white border border-neutral-300 rounded-lg text-sm text-neutral-900 hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent transition-all"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                </select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-8">
              <Spinner size="lg" />
            </div>
          ) : sortedProjects.length > 0 ? (
            <div className="divide-y divide-neutral-200">
              {/* Select All Header */}
              <div className="p-4 bg-neutral-50 border-b border-neutral-200">
                <label className="flex items-center gap-3 cursor-pointer">
                  <button
                    onClick={toggleSelectAll}
                    className="w-5 h-5 flex items-center justify-center"
                  >
                    {selectedProjects.size === sortedProjects.length ? (
                      <CheckSquare className="w-5 h-5 text-secondary-600" />
                    ) : (
                      <Square className="w-5 h-5 text-neutral-400" />
                    )}
                  </button>
                  <span className="text-sm font-medium text-neutral-700">
                    {selectedProjects.size === sortedProjects.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </span>
                </label>
              </div>

              {/* Project Rows */}
              {sortedProjects.map((project) => {
                const caseId = project.caseId || project.id;
                const isSelected = selectedProjects.has(caseId);

                return (
                  <div
                    key={caseId}
                    className={`p-6 transition-colors ${
                      isSelected ? 'bg-secondary-50' : 'hover:bg-neutral-50'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Checkbox */}
                      <button
                        onClick={() => toggleSelectProject(caseId)}
                        className="mt-1 w-5 h-5 flex items-center justify-center flex-shrink-0"
                      >
                        {isSelected ? (
                          <CheckSquare className="w-5 h-5 text-secondary-600" />
                        ) : (
                          <Square className="w-5 h-5 text-neutral-400" />
                        )}
                      </button>

                      {/* Project Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                          {project.name}
                        </h3>
                        {project.description && (
                          <p className="text-sm text-neutral-600 mb-2 line-clamp-2">
                            {project.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-neutral-500">
                          <span>
                            Created {new Date(project.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                          {project.updatedAt && (
                            <>
                              <span>•</span>
                              <span>
                                Deleted {new Date(project.updatedAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(caseId, project.name)}
                          className="text-secondary-600 hover:bg-secondary-50 flex items-center gap-2"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Restore
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleHardDelete(caseId, project.name)}
                          className="text-error hover:bg-error-50 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete Forever
                        </Button>
                        <Badge variant="secondary">Deleted</Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Archive className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                No Deleted Cases
              </h3>
              <p className="text-neutral-600">
                All projects are active. Deleted projects will appear here.
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Confirmation Dialog */}
      {showConfirmDialog.type && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="max-w-md w-full mx-4">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  showConfirmDialog.type === 'restore'
                    ? 'bg-secondary-100'
                    : 'bg-error-100'
                }`}>
                  {showConfirmDialog.type === 'restore' ? (
                    <RefreshCw className="w-5 h-5 text-secondary-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-error" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-neutral-900">
                  {showConfirmDialog.type === 'restore' && 'Restore Project'}
                  {showConfirmDialog.type === 'hard-delete' && 'Permanently Delete Project'}
                  {showConfirmDialog.type === 'bulk-hard-delete' && 'Permanently Delete Multiple Projects'}
                </h3>
              </div>
            </CardHeader>
            <CardBody>
              <p className="text-neutral-600 mb-6">
                {showConfirmDialog.type === 'restore' && (
                  <>Are you sure you want to restore <strong>{showConfirmDialog.projectName}</strong>? It will be reactivated and visible to users.</>
                )}
                {showConfirmDialog.type === 'hard-delete' && (
                  <>Are you sure you want to <strong className="text-error">permanently delete</strong> <strong>{showConfirmDialog.projectName}</strong>? This action cannot be undone and all project data will be lost forever.</>
                )}
                {showConfirmDialog.type === 'bulk-hard-delete' && (
                  <>Are you sure you want to <strong className="text-error">permanently delete {selectedProjects.size} projects</strong>? This action cannot be undone and all project data will be lost forever.</>
                )}
              </p>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmDialog({ type: null })}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant={showConfirmDialog.type === 'restore' ? 'primary' : 'primary'}
                  onClick={confirmAction}
                  className={`flex-1 ${
                    showConfirmDialog.type === 'restore'
                      ? ''
                      : 'bg-error hover:bg-error-600'
                  }`}
                >
                  {showConfirmDialog.type === 'restore' && 'Restore Project'}
                  {(showConfirmDialog.type === 'hard-delete' || showConfirmDialog.type === 'bulk-hard-delete') && 'Delete Forever'}
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}
